import { apiClient } from '@/api/client';
import type { components, ProblemSlug } from '@/api/generated-types';
import { blankPdfBlob } from '@/utils/debug/blankPdf';
import { readSeedableExport, type DebugExport } from '@/utils/debug/seedFromExport';
import {
  jsonBlob,
  reproDisplayName,
  seedMultipart,
  STAND_IN_PDF_NAME,
} from '@/utils/debug/seedRequest';

type SeedTransactionResponse = components['schemas']['SeedTransactionResponse'];

// Admin-only. The endpoint always throws the exception behind `slug`, so this
// call always rejects with the resulting ApiError — that rejection is the point:
// it drives the real problem+json → interceptor → ApiError → toast pipeline.
export async function triggerServerError(slug: ProblemSlug): Promise<never> {
  await apiClient.post('/api/v1/dev/errors/trigger', { slug });
  // Unreachable in practice — the endpoint never returns a success body.
  throw new Error('Dev error trigger returned unexpectedly without throwing.');
}

/**
 * Rebuilds a debug export as a transaction on this environment, and returns its id.
 *
 * Admin-only and gated on `Dev:SeedingEnabled`, so it answers 404 anywhere that hasn't opted in.
 *
 * Takes the picked file rather than a parsed export: the caller passes this through a mutation,
 * whose variables the query cache keeps for minutes afterwards. A `File` is a disk-backed handle;
 * a parsed export of a few thousand cargo items is not, and would be held long after the panel
 * closed. Parsing here keeps the object graph collectable as soon as the body is built.
 */
export async function seedTransactionFromExport(
  exportFile: File,
  sourcePdf: File | null
): Promise<string> {
  const seedable = readSeedableExport(JSON.parse(await exportFile.text()) as DebugExport);

  const form = seedMultipart({
    source: sourcePdf ?? blankPdfBlob(),
    sourceFilename: sourcePdf?.name ?? STAND_IN_PDF_NAME,
    document: jsonBlob(seedable.document),
    layout: seedable.layout ? jsonBlob(seedable.layout) : undefined,
    overlays: Object.fromEntries(
      Object.entries(seedable.overlays).map(([filename, value]) => [filename, jsonBlob(value)])
    ),
    documentType: seedable.documentType,
    displayName: reproDisplayName(seedable.origin),
  });

  const { data } = await apiClient.post<SeedTransactionResponse>('/api/v1/dev/transactions', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!data.transactionId) throw new Error('Seeding returned no transaction id.');
  return data.transactionId;
}
